import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GlobalDataService } from '@services/global-data.service';
import { Router } from '@angular/router';
import { ComposerApiService } from '@services/composer-api.service';
import { FolderApiService } from '@services/folder-api.service';
import { ProductEnrichmentApiService } from '@services/product-enrichment-api.service';
import { GitOperationsApiService } from '@services/git-operations-api.service';
import { ScipComplianceApiService } from '@services/scip-compliance-api.service';
import { ToastrService } from '@services/toaster.service';
import { LocalStorageService } from '@services/local-storage.service';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import { EnrichProductComponent } from '@dialogs/enrich-product/enrich-product.component';
import { cleanupContextualTools } from '@utils/contextual-tool-cleanup';
import { EntityType } from '@mds/common';

@Injectable()
export class MattersmapService {
  private globalDataService = inject(GlobalDataService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private folderApiService = inject(FolderApiService);
  private productEnrichmentApiService = inject(ProductEnrichmentApiService);
  private gitOperationsApiService = inject(GitOperationsApiService);
  private composerApiService = inject(ComposerApiService);
  private scipComplianceApiService = inject(ScipComplianceApiService);
  private toastrService = inject(ToastrService);
  private localStorageService = inject(LocalStorageService);

  constructor(private httpClient: HttpClient) {}

  getTileCards(rid) {
    rid = rid.replace('#', '');
    return this.httpClient.get(`/api/v1/cards/rids?rids=${rid}`);
  }

  getModuleTools(rid: string) {
    return this.httpClient.get(`/api/v1/info/module/composer?rid=${rid}&is_new_formschema=true`).pipe(
      map((response: any) => {
        if (response?.contextualTools?.length) {
          response.contextualTools = cleanupContextualTools(response.contextualTools);
        }
        return response;
      })
    );
  }

  getSidePanelDetailsWithSubModule(rid: string, layer: string, module: string, submodule: string) {
    const query = new URLSearchParams({ module, submodule, layer });
    return this.httpClient.get(`/api/v1/info/${rid}?${query}&is_new_formschema=true`).pipe(
      map((res: any) => {
        if (res?.contextualTools?.length) {
          res.contextualTools = cleanupContextualTools(res.contextualTools);
        }
        return res;
      }),
      catchError(() => of({ contextualTools: [], chipContent: [], modelTools: [] }))
    );
  }

  resumeImport(resumeBomImport: any) {
    this.globalDataService.setResumeImport(resumeBomImport);
    this.router.navigate(['/', 'importer', 'bom_import']);
  }

  getEnrichProduct(rid: string) {
    const data = {
      multiple: true,
      selectOption: false,
      type: 'enrich-search',
      action: 'enrich-product-search',
      rid
    };

    const dialogRef = this.dialog.open(EnrichProductComponent, {
      data,
      height: '70vh',
      width: '50vw'
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.productEnrichmentApiService.enrichProduct(rid, result).subscribe({
          next: () => {
            this.toastrService.showMessage('Product enriched successfully', 3000);
          },
          error: () => {}
        });
      }
    });
  }

  openDatasheet(rid: string) {
    this.globalDataService.setSelectedApp(undefined);
    this.globalDataService.setPpmSelectedItem(undefined);
    this.router.navigate(['/mattermaps/overview/', rid]);
  }

  dataOperation(action: 'duplicate' | 'copy' | 'merge' | 'deploy' | 'fork', rid) {
    if (action === 'duplicate') action = 'copy';
    this.gitOperationsApiService.dataSetOperations(action, rid).subscribe((res: any) => {
      this.toastrService.showMessage('Success', 3000);
      this.router.navigate(['/mattermaps/overview/', res.rid.replace('#', ''), 'edit']);
    });
  }

  pull(rid) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Publish to Makersite',
        text: null,
        cancel_text: 'Cancel',
        publish_text: 'Publish',
        type: 'Addpullrequest'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const obj = {
          key: rid,
          comment: result
        };
        if (rid && obj.comment !== undefined) {
          this.gitOperationsApiService.dataSetOperations('pull', rid, obj).subscribe({
            next: () => {
              this.toastrService.showMessage('Published successfully', 2000);
              this.router.navigateByUrl('/dashboard', { skipLocationChange: true }).then(() => {
                // NOTE: 0ms setTimeout required to fix run order issue where the menuService.selectedAction$ hadnt been cleared before the mattermaps-edit reloaded, causing the pull request dialog to open again
                setTimeout(() => {
                  this.router.navigate(['/mattermaps/overview/', rid, 'edit']);
                }, 0);
              });
            },
            error: () => {}
          });
        }
      }
    });
  }

  openInMcda(rid) {
    this.router.navigate(['mcda', rid]);
  }

  reCalculate(selectedTabName, rid) {
    const tab = selectedTabName;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: `Re-calculate ${tab}`,
        text: `Re-calculating will replace all existing ${tab}. Are you sure?`,
        cancel_text: 'No',
        delete_text: 'Yes'
      }
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;
      if (tab === 'Environmental Impacts') {
        this.composerApiService.reCalculateEI(rid).subscribe({
          next: () => {
            this.toastrService.showMessage('Environmental Impacts recalculated successfully');
          },
          error: () => {}
        });
      } else if (tab === 'Exemptions') {
        this.scipComplianceApiService.reCalculateExemptions(rid).subscribe({
          next: () => {
            this.toastrService.showMessage('Exemptions recalculated successfully');
          },
          error: () => {}
        });
      }
    });
  }

  deleteData(productName, rid, type) {
    const defaultMessage = `Would you like to delete the ${productName} dataset?`;
    const forecastMessage = `Deleting the ${productName} will affect the whole team. This may also impact pre-calculated data and any related downstream results. Confirm that you want to delete this scenario.`;
    // improve in the future for specific entities.
    const text = type === EntityType.SCENARIO_FORECAST ? forecastMessage : defaultMessage;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Delete Item?',
        text,
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;
      this.composerApiService.deleteData(rid).subscribe((res) => {
        const trash = Object.values(res)[0];
        const trashIds = trash.trash_rids;
        if (trash.message === 'success') {
          this.toastrService
            .showWithAction('Deleted Successfully', 'Undo', 5000)
            .afterDismissed()
            .subscribe((restore) => {
              if (!restore.dismissedByAction) return;
              this.folderApiService.restoreItem(trashIds[0]).subscribe({
                next: () => {
                  this.toastrService.showMessage('Restored successfully.');
                  const restoredRid = trashIds[0].replace('#', '');
                  this.router.navigate(['mattermaps/overview/', restoredRid, 'edit']);
                },
                error: () => {}
              });
            });

          this.router.navigate(['dashboard']);
        }
      });
    });
  }

  convertNormalTextToUrl(url: string) {
    return url.trim().toLowerCase().replace(' ', '-');
  }

  inviteDialogNew() {
    this.dialog.open(InviteShareComponent, {
      data: { type: 'showInvite', value: 'Invite' },
      panelClass: 'show-invite'
    });
  }

  shareDialogNew(layer: string, rid: string) {
    const userInfo = this.localStorageService.getItem('user_info');
    const link = `/mattermaps/${layer}/${rid}`;
    const shareData = {
      type: 'showInvite',
      value: 'Share',
      data: {
        fr: userInfo.email,
        link,
        rid,
        usersLackingAccessList: ''
      }
    };
    this.dialog.open(InviteShareComponent, {
      data: shareData,
      panelClass: 'show-invite'
    });
  }
}
